import type { CSSProperties, SVGProps } from 'react';

/**
 * Иконки (lucide-style, 24x24, stroke 2) — порт из docs/prototype/icons.js.
 * Описываются декларативно: строка = <path d>, объект = circle/rect/line.
 */

type PathDef =
  | string
  | ({ kind: 'circle'; cx: number; cy: number; r: number })
  | ({ kind: 'rect'; x: number; y: number; width: number; height: number; rx?: number })
  | ({ kind: 'line'; x1: number; y1: number; x2: number; y2: number });

const c = (cx: number, cy: number, r: number): PathDef => ({ kind: 'circle', cx, cy, r });
const rect = (x: number, y: number, width: number, height: number, rx?: number): PathDef => ({
  kind: 'rect',
  x,
  y,
  width,
  height,
  rx,
});
const line = (x1: number, y1: number, x2: number, y2: number): PathDef => ({
  kind: 'line',
  x1,
  y1,
  x2,
  y2,
});

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'ref'> {
  size?: number;
  style?: CSSProperties;
}

const DEFS: Record<string, { paths: PathDef[]; sw?: number; fill?: boolean }> = {
  Dashboard: { paths: ['M3 3h8v8H3zM13 3h8v5h-8zM13 12h8v9h-8zM3 13h8v8H3z'] },
  Inbox: {
    paths: [
      'M22 12h-6l-2 3h-4l-2-3H2',
      'M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z',
    ],
  },
  Kanban: {
    paths: [rect(3, 3, 6, 14, 1.5), rect(9.5, 3, 6, 9, 1.5), rect(15.5, 3, 5.5, 11, 1.5)],
  },
  Users: {
    paths: [
      'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2',
      c(9, 7, 4),
      'M22 21v-2a4 4 0 0 0-3-3.87',
      'M16 3.13a4 4 0 0 1 0 7.75',
    ],
  },
  Building: {
    paths: [
      rect(4, 2, 16, 20, 2),
      line(9, 7, 9, 7),
      line(15, 7, 15, 7),
      line(9, 11, 9, 11),
      line(15, 11, 15, 11),
      'M9 22v-4h6v4',
    ],
  },
  Plug: { paths: ['M12 22v-5', 'M9 8V2', 'M15 8V2', 'M18 8v4a6 6 0 0 1-12 0V8z'] },
  Tag: {
    paths: [
      'M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z',
      c(7.5, 7.5, 1.2),
    ],
  },
  Sun: {
    paths: [
      c(12, 12, 4),
      'M12 2v2',
      'M12 20v2',
      'm4.93 4.93 1.41 1.41',
      'm17.66 17.66 1.41 1.41',
      'M2 12h2',
      'M20 12h2',
      'm6.34 17.66-1.41 1.41',
      'm19.07 4.93-1.41 1.41',
    ],
  },
  Moon: { paths: ['M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9z'] },
  ChevronDown: { paths: ['m6 9 6 6 6-6'] },
  ChevronUp: { paths: ['m18 15-6-6-6 6'] },
  ChevronRight: { paths: ['m9 18 6-6-6-6'] },
  ChevronLeft: { paths: ['m15 18-6-6 6-6'] },
  LogOut: { paths: ['M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4', 'm16 17 5-5-5-5', line(21, 12, 9, 12)] },
  Plus: { paths: ['M5 12h14', 'M12 5v14'] },
  Search: { paths: [c(11, 11, 8), 'm21 21-4.3-4.3'] },
  Filter: { paths: ['M22 3H2l8 9.46V19l4 2v-8.54L22 3z'] },
  Shield: {
    paths: [
      'M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z',
    ],
  },
  User: { paths: ['M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2', c(12, 7, 4)] },
  Eye: { paths: ['M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z', c(12, 12, 3)] },
  EyeOff: {
    paths: [
      'M9.88 9.88a3 3 0 1 0 4.24 4.24',
      'M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68',
      'M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61',
      'M2 2l20 20',
    ],
  },
  Send: {
    paths: [
      'M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z',
      'm21.854 2.147-10.94 10.939',
    ],
  },
  Paperclip: {
    paths: [
      'm21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48',
    ],
  },
  Message: { paths: ['M7.9 20A9 9 0 1 0 4 16.1L2 22z'] },
  Lock: { paths: [rect(3, 11, 18, 11, 2), 'M7 11V7a5 5 0 0 1 10 0v4'] },
  Clock: { paths: [c(12, 12, 10), 'M12 6v6l4 2'] },
  Alert: {
    paths: [
      'm21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3z',
      line(12, 9, 12, 13),
      line(12, 17, 12, 17),
    ],
  },
  Mail: { paths: [rect(2, 4, 20, 16, 2), 'm22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7'] },
  Telegram: {
    paths: [
      'M21.5 4.5 2.5 11.8c-1 .4-1 1.5 0 1.8l4.8 1.5 1.8 5.7c.2.6.5.7 1 .4l2.8-2 4.7 3.5c.6.4 1.2.2 1.4-.6l3.4-15.9c.3-1.2-.5-1.7-1.6-1.2z',
    ],
  },
  TgPlane: {
    paths: [
      'M9.78 18.65l.28-4.23L17.74 7.5c.34-.3-.07-.45-.52-.18L7.74 13.5 3.56 12.2c-.9-.28-.91-.9.2-1.33l16.31-6.29c.75-.34 1.47.17 1.18 1.33l-2.78 13.1c-.19.94-.76 1.17-1.54.73l-4.25-3.13-2.05 1.98c-.23.23-.42.42-.86.42z',
    ],
    fill: true,
  },
  Check: { paths: ['M20 6 9 17l-5-5'] },
  CheckCircle: { paths: [c(12, 12, 10), 'm9 12 2 2 4-4'] },
  XCircle: { paths: [c(12, 12, 10), 'm15 9-6 6', 'm9 9 6 6'] },
  X: { paths: ['M18 6 6 18', 'M6 6l12 12'] },
  Menu: { paths: [line(4, 6, 20, 6), line(4, 12, 20, 12), line(4, 18, 20, 18)] },
  More: { paths: [c(12, 12, 1), c(19, 12, 1), c(5, 12, 1)] },
  Link: {
    paths: [
      'M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71',
      'M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71',
    ],
  },
  Bell: {
    paths: [
      'M10.268 21a2 2 0 0 0 3.464 0',
      'M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326',
    ],
  },
  BellOff: {
    paths: [
      'M10.268 21a2 2 0 0 0 3.464 0',
      'M17 17H4a1 1 0 0 1-.74-1.673C4.59 13.956 6 12.499 6 8c0-.43.06-.846.17-1.24',
      'M8.668 3.01A6 6 0 0 1 18 8c0 2.687.504 4.413 1.022 5.5',
      line(2, 2, 22, 22),
    ],
  },
  Play: {
    paths: ['M6 4.5v15l13-7.5z'],
  },
  Square: {
    paths: [rect(6, 6, 12, 12, 2.5)],
    fill: true,
  },
  Loader: {
    paths: ['M21 12a9 9 0 1 1-6.219-8.56'],
  },
  Pencil: {
    paths: [
      'M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z',
      'm15 5 4 4',
    ],
  },
  Trash: {
    paths: [
      'M3 6h18',
      'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6',
      'M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2',
      line(10, 11, 10, 17),
      line(14, 11, 14, 17),
    ],
  },
  External: {
    paths: ['M15 3h6v6', 'M10 14 21 3', 'M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6'],
  },
  Activity: {
    paths: [
      'M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2',
    ],
  },
  Hash: { paths: [line(4, 9, 20, 9), line(4, 15, 20, 15), line(10, 3, 8, 21), line(16, 3, 14, 21)] },
  Calendar: {
    paths: [rect(3, 4, 18, 18, 2), line(16, 2, 16, 6), line(8, 2, 8, 6), line(3, 10, 21, 10)],
  },
  ArrowRight: { paths: ['M5 12h14', 'm12 5 7 7-7 7'] },
  ArrowUp: { paths: ['m5 12 7-7 7 7', 'M12 19V5'] },
  Reply: { paths: ['M9 17l-5-5 5-5', 'M4 12h11a4 4 0 0 1 4 4v3'] },
  Bug: {
    paths: [
      'm8 2 1.88 1.88',
      'M14.12 3.88 16 2',
      'M9 7.13v-1a3.003 3.003 0 1 1 6 0v1',
      'M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6',
      'M12 20v-9',
      'M6.53 9C4.6 8.8 3 7.1 3 5',
      'M6 13H2',
      'M3 21c0-2.1 1.7-3.9 3.8-4',
      'M20.97 5c0 2.1-1.6 3.8-3.5 4',
      'M22 13h-4',
      'M17.2 17c2.1.1 3.8 1.9 3.8 4',
    ],
  },
  Sparkles: {
    paths: [
      'M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .962 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.962 0z',
    ],
  },
  Code: { paths: ['m16 18 6-6-6-6', 'm8 6-6 6 6 6'] },
  Key: { paths: [c(7.5, 15.5, 5.5), 'm21 2-9.6 9.6', 'm15.5 7.5 3 3L22 7l-3-3'] },
  Help: { paths: [c(12, 12, 10), 'M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3', line(12, 17, 12, 17)] },
  Image: { paths: [rect(3, 3, 18, 18, 2), c(9, 9, 2), 'm21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21'] },
  ImageOff: {
    paths: [
      line(2, 2, 22, 22),
      'M10.41 10.41a2 2 0 1 1-2.83-2.83',
      'M13.5 13.5 6 21',
      'M18 12l-1.586-1.586a2 2 0 0 0-2.828 0L11 13',
      'M21 15V5a2 2 0 0 0-2-2H9',
      'M3 5.5V19a2 2 0 0 0 2 2h13.5',
    ],
  },
  File: { paths: ['M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z', 'M14 2v6h6'] },
  FileText: {
    paths: [
      'M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z',
      'M14 2v6h6',
      line(8, 13, 16, 13),
      line(8, 17, 16, 17),
      line(8, 9, 10, 9),
    ],
  },
  Sheet: {
    paths: [
      rect(3, 3, 18, 18, 2),
      line(3, 9, 21, 9),
      line(3, 15, 21, 15),
      line(9, 3, 9, 21),
      line(15, 3, 15, 21),
    ],
  },
  Archive: {
    paths: [
      rect(2, 4, 20, 5, 1),
      'M4 9v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9',
      'M10 13h4',
    ],
  },
  Download: { paths: ['M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4', 'm7 10 5 5 5-5', line(12, 15, 12, 3)] },
  ZoomIn: { paths: [c(11, 11, 8), 'm21 21-4.3-4.3', line(11, 8, 11, 14), line(8, 11, 14, 11)] },
  Dot: { paths: [c(12, 12, 3)], sw: 0 },
  Flag: { paths: ['M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z', line(4, 22, 4, 15)] },
  Settings: {
    paths: [
      'M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z',
      c(12, 12, 3),
    ],
  },
  Globe: { paths: [c(12, 12, 10), 'M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20', 'M2 12h20'] },
  Server: {
    paths: [rect(2, 3, 20, 8, 2), rect(2, 13, 20, 8, 2), line(6, 7, 6, 7), line(6, 17, 6, 17)],
  },
};

export type IconName = keyof typeof DEFS;

export function Icon({ name, size = 18, style, ...rest }: { name: IconName } & IconProps) {
  const def = DEFS[name];
  if (!def) return null;
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill={def.fill ? 'currentColor' : 'none'}
      stroke={def.fill ? 'none' : 'currentColor'}
      strokeWidth={def.sw ?? 2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
      aria-hidden
      {...rest}
    >
      {def.paths.map((d, i) => {
        if (typeof d === 'string') return <path key={i} d={d} />;
        if (d.kind === 'circle') return <circle key={i} cx={d.cx} cy={d.cy} r={d.r} />;
        if (d.kind === 'rect')
          return <rect key={i} x={d.x} y={d.y} width={d.width} height={d.height} rx={d.rx} />;
        return <line key={i} x1={d.x1} y1={d.y1} x2={d.x2} y2={d.y2} />;
      })}
    </svg>
  );
}

/** Безопасно ли имя иконки (для динамических ключей из meta). */
// eslint-disable-next-line react-refresh/only-export-components
export function hasIcon(name: string): name is IconName {
  return name in DEFS;
}
